import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { TrendingUp, Download, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const NZD = (v) =>
  (Number(v) || 0).toLocaleString("en-NZ", {
    style: "currency", currency: "NZD", maximumFractionDigits: 2,
  });

const fmtHrs = (v) => `${(Number(v) || 0).toFixed(1)} hrs`;
const fmtSignedHrs = (v) => `${v > 0 ? "+" : ""}${(Number(v) || 0).toFixed(1)} hrs`;
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  // Default: current week (Mon → today)
  const today = new Date();
  const weekday = (today.getDay() + 6) % 7;
  const mon = new Date(today);
  mon.setDate(today.getDate() - weekday);
  return { from: isoDate(mon), to: isoDate(today) };
}

// Severity colour for variance — Green ≤5% & ≤1h · Amber 5-15% or 1-2h · Red >15% or >2h.
function severityFor(varHours, varPct) {
  const absH = Math.abs(Number(varHours) || 0);
  const absP = Math.abs(Number(varPct) || 0);
  if (absH > 2 || absP > 15) return "red";
  if (absH > 1 || absP > 5) return "amber";
  return "green";
}

const SEVERITY_BG = {
  green: "bg-emerald-50 text-emerald-800 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-red-50 text-red-800 border-red-200",
};
const SEVERITY_TXT = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red: "text-red-600 font-semibold",
};

export default function VarianceReport() {
  const [params, setParams] = useState(() => {
    const r = defaultRange();
    return { date_from: r.from, date_to: r.to, view: "daily" };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showByEmployee, setShowByEmployee] = useState(false);
  const [empSortDesc, setEmpSortDesc] = useState(true); // largest +variance first

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/variance", { params });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load variance");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const stripSeverity = severityFor(summary?.variance_hours, summary?.variance_pct);

  const sortedEmployees = useMemo(() => {
    if (!data?.employees) return [];
    const rows = [...data.employees];
    rows.sort((a, b) =>
      empSortDesc ? b.variance_hours - a.variance_hours : a.variance_hours - b.variance_hours,
    );
    return rows;
  }, [data, empSortDesc]);

  const csvUrl = `${API_BASE}/admin/variance.csv?date_from=${params.date_from}&date_to=${params.date_to}&view=${params.view}`;

  return (
    <div className="space-y-6" data-testid="variance-report-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">
            Hours variance
          </h1>
          <p className="text-gray-500 mt-1">
            Compare actual worked hours against rostered hours per employee. Cost impact is the variance multiplied by each employee&apos;s hourly rate.
          </p>
        </div>
      </header>

      {/* Filters */}
      <Card className="border-gray-200">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">From</Label>
              <Input
                type="date"
                value={params.date_from}
                onChange={(e) => setParams((p) => ({ ...p, date_from: e.target.value }))}
                data-testid="variance-date-from"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">To</Label>
              <Input
                type="date"
                value={params.date_to}
                onChange={(e) => setParams((p) => ({ ...p, date_to: e.target.value }))}
                data-testid="variance-date-to"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Period</Label>
              <Select
                value={params.view}
                onValueChange={(v) => setParams((p) => ({ ...p, view: v }))}
              >
                <SelectTrigger data-testid="variance-view"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={load} disabled={loading} className="bg-[#1A2744] text-white hover:bg-[#2A3855]" data-testid="variance-refresh">
                {loading ? "Loading…" : "Refresh"}
              </Button>
              <a href={csvUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="variance-export-csv">
                  <Download className="h-4 w-4 mr-1" /> Export
                </Button>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary strip */}
      {summary && (
        <Card className={`border ${SEVERITY_BG[stripSeverity]}`} data-testid="variance-summary">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Period</div>
                <div className="font-heading text-lg font-bold">{params.date_from} → {params.date_to}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Rostered</div>
                <div className="font-heading text-xl font-bold">{fmtHrs(summary.rostered_hours)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Actual</div>
                <div className="font-heading text-xl font-bold">{fmtHrs(summary.actual_hours)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Variance</div>
                <div className="font-heading text-xl font-bold" data-testid="variance-total-hours">{fmtSignedHrs(summary.variance_hours)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Variance %</div>
                <div className="font-heading text-xl font-bold" data-testid="variance-total-pct">{fmtPct(summary.variance_pct)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold">Cost impact</div>
                <div className="font-heading text-xl font-bold" data-testid="variance-cost-impact">{NZD(summary.cost_impact)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period (day-level) table */}
      <Card className="border-gray-200">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">By {params.view.replace(/y$/, "y")} period</h3>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="variance-period-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Rostered</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead className="text-right">%</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.periods?.map((p) => {
                  const sev = severityFor(p.variance_hours, p.variance_pct);
                  return (
                    <TableRow key={p.key} data-testid={`variance-row-${p.key}`}>
                      <TableCell className="font-medium">{p.label}</TableCell>
                      <TableCell className="text-right font-mono">{fmtHrs(p.rostered_hours)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtHrs(p.actual_hours)}</TableCell>
                      <TableCell className={`text-right font-mono ${SEVERITY_TXT[sev]}`}>
                        {fmtSignedHrs(p.variance_hours)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${SEVERITY_TXT[sev]}`}>
                        {fmtPct(p.variance_pct)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{NZD(p.cost_impact)}</TableCell>
                    </TableRow>
                  );
                })}
                {(!data || data.periods?.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-sm text-gray-500 text-center py-6">
                      No data for the selected period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {data?.periods?.length > 0 && summary && (
                <TableFooter>
                  <TableRow className="font-semibold bg-gray-50">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono">{fmtHrs(summary.rostered_hours)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtHrs(summary.actual_hours)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtSignedHrs(summary.variance_hours)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtPct(summary.variance_pct)}</TableCell>
                    <TableCell className="text-right font-mono">{NZD(summary.cost_impact)}</TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Employee breakdown */}
      <Card className="border-gray-200">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">By employee</h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowByEmployee((v) => !v)}
                data-testid="variance-toggle-employees"
              >
                {showByEmployee ? "Hide" : "Show by employee"}
              </Button>
              {showByEmployee && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmpSortDesc((v) => !v)}
                  data-testid="variance-sort-toggle"
                >
                  {empSortDesc ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronUp className="h-3 w-3 mr-1" />}
                  Largest {empSortDesc ? "+over" : "−under"} first
                </Button>
              )}
            </div>
          </div>
          {showByEmployee && (
            <div className="overflow-x-auto">
              <Table data-testid="variance-employee-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Rostered</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead className="text-right">Avg/shift</TableHead>
                    <TableHead className="text-right">Shifts</TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEmployees.map((e) => {
                    const sev = severityFor(e.variance_hours, e.variance_pct);
                    const flagged = (e.variance_pct ?? 0) > 15;
                    const visaFlag = e.visa_type && e.visa_type.includes("AEWV") && e.actual_hours > 30;
                    return (
                      <TableRow key={e.user_id} data-testid={`variance-emp-${e.user_id}`}>
                        <TableCell className="font-medium">{e.name}</TableCell>
                        <TableCell className="text-xs text-gray-500">{e.employment_type || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{fmtHrs(e.rostered_hours)}</TableCell>
                        <TableCell className="text-right font-mono">{fmtHrs(e.actual_hours)}</TableCell>
                        <TableCell className={`text-right font-mono ${SEVERITY_TXT[sev]}`}>{fmtSignedHrs(e.variance_hours)}</TableCell>
                        <TableCell className={`text-right font-mono ${SEVERITY_TXT[sev]}`}>{fmtPct(e.variance_pct)}</TableCell>
                        <TableCell className="text-right font-mono">{fmtSignedHrs(e.avg_variance_per_shift_hours)}</TableCell>
                        <TableCell className="text-right font-mono">{e.shifts_count}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {flagged && (
                              <Badge variant="outline" className="text-red-700 border-red-300">
                                <AlertTriangle className="h-3 w-3 mr-0.5" /> {">"}15%
                              </Badge>
                            )}
                            {visaFlag && (
                              <Badge variant="outline" className="text-amber-700 border-amber-300">
                                AEWV cap
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {sortedEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-sm text-gray-500 text-center py-6">
                        No employees for the selected period.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
